import { Component, Input, TemplateRef } from '@angular/core';
import { GlobalFunctions } from '@app/Global/GlobalFunctions';
import { animate, style, transition, trigger } from '@angular/animations';
import { ApiService } from '@app/Services/APIService';
import { NotifyService } from '@app/Services/NotifyService';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LoanIndex } from '../Loan/LoanIndex/LoanIndex';

// Interface for Excel row data with selection and status
interface ExcelRowData {
  data: Record<string, unknown>;
  isSelected: boolean;
  status: string;
  rowStatus: string;
  rowIndex: number;
}

export interface ValidationResponse {
  rowIndex: number;
  message: string;
}

//Response from the API response to be used later on
const validationResponses: ValidationResponse[] = [];

// Upload type enum
export enum UploadType {
  SecurityUpload = 'SecurityUpload',
  ValuationUpdate = 'ValuationUpdate'
}

@Component({
  selector: 'SecurityDataUpload',
  templateUrl: './SecurityDataUpload.html',
  styleUrls: ['./SecurityDataUpload.scss'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: '0' }),
        animate('0.1s ease-out', style({ opacity: '1' })),
      ]),
    ]),
  ]
})

export class SecurityDataUploadComponent {

  @Input() LoanIndex: LoanIndex;
  public showExcelFormat = false;

  //Unique Modal Identifer
  public ModalIdentifier;

  // Modal Identifier for Excel Format
  public ExcelFormatModalIdentifier: string;
  public ValidateAndUploadModalIdentifier: string;
  public SelectedFile: any | null = null;
  public FileName: string = '';
  public ExcelData: ExcelRowData[] = [];
  public ColumnHeaders: string[] = [];
  public IsFileImported: boolean = false;
  public IsValidated: boolean = false;
  public HasDataToProcess: boolean = false;
  public ErrorMessage: string = '';
  public WarningMessage: string = '';
  public ExtraColumns: string[] = [];
  public MissingColumns: string[] = [];
  public InvalidColumns: string[] = [];
  public ExcludedRowsCount: number = 0;
  public MissingLoanIDCount: number = 0;
  public TotalRowsInFile: number = 0;
  public AllRowsSelected: boolean = true;
  public IsProcessing: boolean = false;
  public ProcessingComplete: boolean = false;
  public ModalTitle: string = 'Security Property Data Upload';
  public UploadType: UploadType = UploadType.SecurityUpload;
  public LenderList = [];
  public LoanIDMismatchText = "Missing Loan ID";
  public XLSX;
  public HideSampleButton = false;

  //Dialog reference
  public DialogRef;

  //File Upload Data
  public FileUploadData;

  //LoanID
  public LoanID = "";

  //Flag to indicate if valid inputs
  public AreValidInputs: boolean = false;

  // Expected headers based on upload type
  private ExpectedHeaders: string[] = [];

  // Expected column headers in exact order
  private readonly ExpectedSecurityUploadHeaders: string[] = [
    'Loan ID',
    'Property Name',
    'Suite',
    'Floor/Level',
    'Unit',
    'Lot',
    'Number',
    'Street Name',
    'Street Type',
    'City/Suburb',
    'State',
    'Postcode',
    'Country',
    'Purpose',
    'Type',
    'Title Type',
    'Use',
    'Valuer Company',
    'Valuer name',
    'Valuation amount',
    'Insurable Value',
    'Valuation date',
    'Insurer',
    'Policy No.',
    'Policy Type',
    //'Policy Start date',
    'Policy End Date',
    'Insured amount',
    'Insurance Interested party',
    'Letter to be Emailed'
  ];

  // Expected column headers for Valuation Update uploads
  private readonly ExpectedValuationUpdateHeaders: string[] = [
    'Loan ID',
    'Property Name',
    'Suite',
    'Floor',
    'Unit',
    'Number',
    'Street Name',
    'Street Type',
    'City/Suburb',
    'State',
    'Postcode',
    'Country',
    'Valuation Date',
    'Valuation Amount',
    'Valuer Company',
    'Valuer Name'
  ];
  clientDataStore: any;

  constructor(public dialogRef: MatDialogRef<SecurityDataUploadComponent>
    , private apiService: ApiService
    , private notifyService: NotifyService
    , public globalFunctions: GlobalFunctions = null
    , private dialog: MatDialog
  ) {

    // Initialize the headers
    this.ExpectedHeaders_Set();
  }

  //Angular startup method
  async ngOnInit() {

    //Get the loan ID from Loan Context
    this.LoanID = this.LoanIndex.AccountID;
    this.HideSampleButton = true;

    //Lazy load the xlsx bundle
    this.XLSX = await import(/* webpackChunkName: "xlsx-chunk" */ 'xlsx');
  }

  //Downloads the sample csv file
  public async DownloadSampleCSV() {



    //Get expected headers
    const headers = this.Get_ExpectedHeaders();

    const data = [
      headers
    ];

    //,headers.map(() => 'Sample Value')

    //Convert an array of arrays of JS data to a worksheet
    const worksheet = this.XLSX.utils.aoa_to_sheet(data);

    //Let's create a new workbook
    const workbook = this.XLSX.utils.book_new();

    //Now append the worksheet to the workbook
    this.XLSX.utils.book_append_sheet(workbook, worksheet, 'SecurityData');

    //Write workbook data to a file
    this.XLSX.writeFile(workbook, 'ExpectedExcelFormat.xlsx');
  }

  //Launch excel format modal
  public ExcelFormatModal_Open(template: TemplateRef<any>) {

    //Generate a unique identifier
    this.ExcelFormatModalIdentifier = this.globalFunctions.GenerateFastGUID();

    //Wrapper needed to avoid circular dependencies
    this.DialogRef = this.globalFunctions.FeatureModal_Launch(template, this.globalFunctions.GetFeatureModalConfig('100%'), this.dialog, "View Excel File Format", 0, true, false, this.ExcelFormatModalIdentifier);
  }

  //Launch excel format modal
  public ValidateAndUploadModal_Open(template: TemplateRef<any>) {

    //Generate a unique identifier
    this.ValidateAndUploadModalIdentifier = this.globalFunctions.GenerateFastGUID();

    //Wrapper needed to avoid circular dependencies
    this.DialogRef = this.globalFunctions.FeatureModal_Launch(template, this.globalFunctions.GetFeatureModalConfig('100%'), this.dialog, "Validate And Upload Data", 0, true, false, this.ValidateAndUploadModalIdentifier);
  }

  // Initialize the component with upload type and set appropriate headers
  public UploadType_Initialize(uploadType: UploadType): void {
    this.UploadType = uploadType;
    this.ExpectedHeaders_Set();
  }

  //Process/Validate/Prep file for upload
  public FileSelected_Process(event: Event, fileUpload) {

    //Model update (ModelDataUnit_Update) will be done via callback
    this.FileUploadData = this.globalFunctions.FileSelected_Process(event, fileUpload, this.notifyService, this);
  }

  // Set expected headers and modal title based on upload type
  private ExpectedHeaders_Set(): void {
    switch (this.UploadType) {
      case UploadType.SecurityUpload:
        this.ExpectedHeaders = this.ExpectedSecurityUploadHeaders;
        this.ModalTitle = 'Security Property Data Upload';
        break;
      case UploadType.ValuationUpdate:
        this.ExpectedHeaders = this.ExpectedValuationUpdateHeaders;
        this.ModalTitle = 'Valuation Update Upload';
        break;
      default:
        this.ExpectedHeaders = this.ExpectedSecurityUploadHeaders;
        this.ModalTitle = 'Security Property Data Upload';
        break;
    }
  }

  // Get expected headers for display in template
  public Get_ExpectedHeaders(): string[] {
    return this.ExpectedHeaders.length > 0 ? this.ExpectedHeaders : this.ExpectedSecurityUploadHeaders;
  }

  // Handle file selection
  public File_OnSelected(event: Event): void {

    //Get the Primeng target file
    const file = event["currentFiles"][0] as HTMLInputElement;

    if (file !== undefined && file !== null) {

      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];

      if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        this.ErrorMessage = 'Please select a valid Excel file (.xlsx or .xls)';
        return;
      }

      this.SelectedFile = file;
      this.FileName = file.name;
      this.ErrorMessage = '';
    }
  }

  // Upload and parse the Excel file
  public async File_Import(template: TemplateRef<any>): Promise<void> {
    if (this.SelectedFile === null || this.SelectedFile === undefined) {
      this.ErrorMessage = 'Please select a file first';
      return;
    }

    const fileReader = new FileReader();

    fileReader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const arrayBuffer = e.target?.result;
        const workbook = this.XLSX.read(arrayBuffer, { type: 'array', cellDates: true });

        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON
        const jsonData = this.XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];

        if (jsonData.length > 0) {
          // Extract headers (first row)
          const fileHeaders = (jsonData[0] as unknown[]).map((header: unknown) =>
            header !== null && header !== undefined ? String(header).trim() : ''
          );

          // Set expected headers based on upload type
          this.ExpectedHeaders = this.UploadType === UploadType.SecurityUpload
            ? this.ExpectedSecurityUploadHeaders
            : this.ExpectedValuationUpdateHeaders;

          // Validate headers against expected format
          const validationResult = this.Validate_Headers(fileHeaders);

          // Store validation results
          this.MissingColumns = validationResult.missingColumns;
          this.InvalidColumns = validationResult.invalidColumns;
          this.ExtraColumns = validationResult.extraColumns;

          // Set warning message if there are extra columns
          if (this.ExtraColumns.length > 0) {
            this.WarningMessage = `Additional columns detected and will be ignored in processing: ${this.ExtraColumns.join(', ')}`;
          }

          // If there are missing or invalid required columns, stop processing
          if (this.MissingColumns.length > 0 || this.InvalidColumns.length > 0) {
            this.ErrorMessage = 'The Excel file does not match the expected format. Please check the column headers.';
            return;
          }

          // Use expected headers plus extra columns (for display purposes)
          this.ColumnHeaders = [...this.ExpectedHeaders, ...this.ExtraColumns];
          // Extract data rows
          const dataRows = jsonData.slice(1);

          // Track total rows in file (excluding header)
          this.TotalRowsInFile = dataRows.length;

          // Find the index of the Loan ID column
          const loanIDIndex = fileHeaders.findIndex(h => h === 'Loan ID');

          //Find total number of row that are missing loan ID
          this.MissingLoanIDCount = dataRows.filter((row: unknown[]) => {
            const loanID = loanIDIndex !== -1 ? row[loanIDIndex] : null;

            return (
              loanID === null ||
              loanID === undefined ||
              String(loanID).trim() === ''
            );
          }).length;

          //Map the data
          this.ExcelData = dataRows.map((row: unknown[], index: number) => {
            const rowObject: Record<string, unknown> = {};

            this.ColumnHeaders.forEach((header) => {
              const fileHeaderIndex = fileHeaders.findIndex(h => h === header);

              if (fileHeaderIndex !== -1) {
                rowObject[header] =
                  row[fileHeaderIndex] !== null && row[fileHeaderIndex] !== undefined
                    ? row[fileHeaderIndex]
                    : '';
              } else {
                rowObject[header] = '';
              }
            });

            const loanID =
              loanIDIndex !== -1 ? row[loanIDIndex] : null;

            //To set an error message
            let errorMessage = "";

            //Check if we have a loan ID
            const hasLoanId =
              loanID !== null &&
              loanID !== undefined &&
              String(loanID).trim() !== '';

            //Let's check if we have a valid loan id
            const isValidLoanID =
              loanID !== null &&
              loanID !== undefined &&
              String(loanID).trim() === String(this.LoanIndex.AccountID);

            //Set a correct message here
            if (!hasLoanId) {
              errorMessage = "Missing Loan ID";
            }
            else if (!isValidLoanID) {
              errorMessage = "Invalid Loan ID";
            }

            return {
              data: rowObject,
              isSelected: hasLoanId && isValidLoanID, // optional: only select valid rows by default
              status: hasLoanId && isValidLoanID
                ? 'Ready for Processing'
                : errorMessage,
              rowStatus: hasLoanId && isValidLoanID
                ? 'status-ready'
                : 'status-error',
              rowIndex: index + 1
            };
          });

          // Calculate excluded rows count
          this.ExcludedRowsCount = this.ExcelData.filter(row => row.status != 'Ready for Processing').length;

          this.IsFileImported = true;
          this.ErrorMessage = '';

          //We have data to process,ideally we should be able to rely on just ExcludedRowsCount = 0 check
          //this.IsFileImported && this.ExcelData.length > 0 && !this.IsProcessing && this.MissingLoanIDCount === 0 &&
          this.HasDataToProcess = this.ExcludedRowsCount === 0;

        } else {
          this.ErrorMessage = 'The Excel file is empty';
        }
      } catch (error) {
        this.ErrorMessage = 'Error reading Excel file. Please ensure it is a valid Excel file.';
        console.error('Error parsing Excel file:', error);
      }
    };

    fileReader.onerror = () => {
      this.ErrorMessage = 'Error reading file';
    };

    fileReader.readAsArrayBuffer(this.SelectedFile);

    //Let's launch actual validation and upload modal here
    this.ValidateAndUploadModal_Open(template);
  }

  //Download excel file data because of validation errors
  public async Download_Data(): Promise<void> {

    if (!this.ExcelData || this.ExcelData.length === 0) {
      return;
    }

    // Build export rows
    const exportData = this.ExcelData.map(row => ({
      Status: row.status,
      'Row Number': row.rowIndex,
      ...row.data,
    }));

    // Create worksheet
    const worksheet = this.XLSX.utils.json_to_sheet(exportData);

    // Create workbook
    const workbook = this.XLSX.utils.book_new();
    this.XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

    // Generate filename with current date/time
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:]/g, '')
      .replace('T', '_')
      .split('.')[0];

    //Create a file for downloading
    this.XLSX.writeFile(workbook, `ValidatedData_${timestamp}.xlsx`);

  }

  // Validate headers against expected format
  private Validate_Headers(fileHeaders: string[]): {
    isValid: boolean;
    missingColumns: string[];
    extraColumns: string[];
    invalidColumns: string[];
  } {
    const missingColumns: string[] = [];
    const extraColumns: string[] = [];
    const invalidColumns: string[] = [];

    // Check for missing required columns
    this.ExpectedHeaders.forEach(expectedHeader => {
      if (!fileHeaders.includes(expectedHeader)) {
        missingColumns.push(expectedHeader);
      }
    });

    // Check for extra columns not in expected format
    fileHeaders.forEach(fileHeader => {
      if (fileHeader !== '' && !this.ExpectedHeaders.includes(fileHeader)) {
        extraColumns.push(fileHeader);
      }
    });

    // Check for columns with similar names but wrong format (case-sensitive)
    fileHeaders.forEach(fileHeader => {
      const lowercaseFileHeader = fileHeader.toLowerCase();
      const matchingExpected = this.ExpectedHeaders.find(
        expected => expected.toLowerCase() === lowercaseFileHeader && expected !== fileHeader
      );
      if (matchingExpected !== undefined) {
        invalidColumns.push(`"${fileHeader}" should be "${matchingExpected}"`);
      }
    });

    const isValid = missingColumns.length === 0 && invalidColumns.length === 0;

    return {
      isValid,
      missingColumns,
      extraColumns,
      invalidColumns
    };
  }
  // Check if a column is an extra column (not in expected format)
  public Column_IsExtra(columnName: string): boolean {
    return this.ExtraColumns.includes(columnName);
  }

  // Toggle selection for all rows
  public ToggleAll_Selection(): void {
    this.AllRowsSelected = !this.AllRowsSelected;
    this.ExcelData.forEach(row => {
      row.isSelected = this.AllRowsSelected;
    });
  }

  // Toggle selection for a single row
  public Row_ToggleSelection(row: ExcelRowData): void {
    row.isSelected = !row.isSelected;
    // Update AllRowsSelected based on whether all rows are selected
    this.AllRowsSelected = this.ExcelData.every(r => r.isSelected);
  }

  // Get count of selected rows
  public Get_SelectedRowsCount(): number {
    return this.ExcelData.filter(row => row.isSelected).length;
  }

  // Load sample pre-existing data for testing
  public Load_SampleData(): void {
    // Set up column headers to match expected format
    this.ColumnHeaders = [...this.ExpectedHeaders];

    // Create sample data rows
    const sampleRows = [
      {
        'Lender ID': 'L001',
        'Loan ID': 'LN123456',
        'Property Name': 'Sample Property 1',
        'Suite': '101',
        'Floor': '1',
        'Unit': 'A',
        'Number': '123',
        'Street Name': 'Main',
        'Street Type': 'Street',
        'City/Suburb': 'Sydney',
        'State': 'NSW',
        'Postcode': '2000',
        'Country': 'Australia',
        'Purpose': 'Investment',
        'Type': 'Residential',
        'Title Type': 'Torrens',
        'Use': 'Rental',
        'Valuer Company': 'ABC Valuations',
        'Valuer name': 'John Smith',
        'Valuation amount': '750000',
        'Insurable Value': '800000',
        'Valuation date': '15/03/2026',
        'Insurer': 'XYZ Insurance',
        'Policy No.': 'POL12345',
        'Policy Type': 'Building',
        'Policy Start date': '01/01/2026',
        'Policy End Date': '31/12/2026',
        'Insured amount': '800000',
        'Insurance Interested party': 'ABC Bank',
        'Letter to be Emailed': 'Yes'
      },
      {
        'Lender ID': 'L002',
        'Loan ID': 'LN789012',
        'Property Name': 'Sample Property 2',
        'Suite': '202',
        'Floor': '2',
        'Unit': 'B',
        'Number': '456',
        'Street Name': 'George',
        'Street Type': 'Avenue',
        'City/Suburb': 'Melbourne',
        'State': 'VIC',
        'Postcode': '3000',
        'Country': 'Australia',
        'Purpose': 'Owner Occupied',
        'Type': 'Commercial',
        'Title Type': 'Strata',
        'Use': 'Office',
        'Valuer Company': 'DEF Valuers',
        'Valuer name': 'Jane Doe',
        'Valuation amount': '1200000',
        'Insurable Value': '1250000',
        'Valuation date': '20/03/2026',
        'Insurer': 'DEF Insurance',
        'Policy No.': 'POL67890',
        'Policy Type': 'Contents',
        'Policy Start date': '01/02/2026',
        'Policy End Date': '31/01/2027',
        'Insured amount': '1250000',
        'Insurance Interested party': 'XYZ Bank',
        'Letter to be Emailed': 'No'
      },
      {
        'Lender ID': 'L003',
        'Loan ID': 'LN345678',
        'Property Name': 'Sample Property 3',
        'Suite': '',
        'Floor': '',
        'Unit': '',
        'Number': '789',
        'Street Name': 'Elizabeth',
        'Street Type': 'Road',
        'City/Suburb': 'Brisbane',
        'State': 'QLD',
        'Postcode': '4000',
        'Country': 'Australia',
        'Purpose': 'Investment',
        'Type': 'Residential',
        'Title Type': 'Torrens',
        'Use': 'Holiday',
        'Valuer Company': 'GHI Appraisals',
        'Valuer name': 'Bob Johnson',
        'Valuation amount': '950000',
        'Insurable Value': '1000000',
        'Valuation date': '22/03/2026',
        'Insurer': 'GHI Insurance',
        'Policy No.': 'POL11111',
        'Policy Type': 'Building & Contents',
        'Policy Start date': '15/03/2026',
        'Policy End Date': '14/03/2027',
        'Insured amount': '1000000',
        'Insurance Interested party': 'DEF Bank',
        'Letter to be Emailed': 'Yes'
      }
    ];

    // Map sample data to ExcelRowData format with status 2 and 3
    this.ExcelData = sampleRows.map((rowData, index) => ({
      data: rowData,
      isSelected: true,
      status: index === 0 ? 'Status 2' : index === 1 ? 'Status 3' : 'Ready for Processing',
      rowStatus: "status-ready",
      rowIndex: index
    }));

    // Set tracking values
    this.TotalRowsInFile = sampleRows.length;
    this.ExcludedRowsCount = 0;
    this.IsFileImported = true;
    this.AllRowsSelected = true;
    this.ErrorMessage = '';
    this.WarningMessage = '';
    this.ExtraColumns = [];
    this.MissingColumns = [];
    this.InvalidColumns = [];
    this.MissingLoanIDCount = 0;
  }

  // Get existing data for comparison (simulates database data)
  private Get_ExistingData(): Record<string, unknown>[] {
    return [
      {
        'Lender ID': 'L001',
        'Loan ID': 'LN123456',
        'Property Name': 'Sample Property 1',
        'Suite': '101',
        'Floor': '1',
        'Unit': 'A',
        'Number': '123',
        'Street Name': 'Collins',
        'Street Type': 'Street',
        'City/Suburb': 'Sydney',
        'State': 'NSW',
        'Postcode': '2000',
        'Country': 'Australia',
        'Purpose': 'Investment',
        'Type': 'Residential',
        'Title Type': 'Torrens',
        'Use': 'Rental',
        'Valuer Company': 'ABC Valuers',
        'Valuer name': 'John Smith',
        'Valuation amount': '850000',
        'Insurable Value': '900000',
        'Valuation date': '15/03/2026',
        'Insurer': 'ABC Insurance',
        'Policy No.': 'POL12345',
        'Policy Type': 'Building',
        'Policy Start date': '01/01/2026',
        'Policy End Date': '31/12/2026',
        'Insured amount': '900000',
        'Insurance Interested party': 'ABC Bank',
        'Letter to be Emailed': 'Yes'
      },
      {
        'Lender ID': 'L004',
        'Loan ID': 'LN999888',
        'Property Name': 'Test Property Alpha',
        'Suite': '',
        'Floor': '',
        'Unit': '',
        'Number': '999',
        'Street Name': 'Test',
        'Street Type': 'Lane',
        'City/Suburb': 'Perth',
        'State': 'WA',
        'Postcode': '6000',
        'Country': 'Australia',
        'Purpose': 'Owner Occupied',
        'Type': 'Residential',
        'Title Type': 'Strata',
        'Use': 'Primary Residence',
        'Valuer Company': 'Test Valuers',
        'Valuer name': 'Test Person',
        'Valuation amount': '750000',
        'Insurable Value': '800000',
        'Valuation date': '01/03/2026',
        'Insurer': 'Test Insurance',
        'Policy No.': 'POL99999',
        'Policy Type': 'Contents',
        'Policy Start date': '01/02/2026',
        'Policy End Date': '31/01/2027',
        'Insured amount': '800000',
        'Insurance Interested party': 'Test Bank',
        'Letter to be Emailed': 'No'
      }
    ];
  }

  // Check if two rows match (compare key fields)
  private Rows_AreEqual(row1: Record<string, unknown>, row2: Record<string, unknown>): boolean {
    // Compare key fields: Lender ID, Loan ID, and Property Name
    return row1['Lender ID'] === row2['Lender ID'] &&
      row1['Loan ID'] === row2['Loan ID'] &&
      row1['Property Name'] === row2['Property Name'];
  }

  // Convert file to Base64
  private async File_ToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }

  //Disable submit button
  public CSS_Disable(): string {

    if (!this.SelectedFile) {
      return "glb_clearButton";
    }
    return "";
  }

  //Validate the data
  public async Validate_Data() {
    //TODO:Review following line when implementing server side code changes
    this.IsValidated = true;
  }

  // Process the upload - send to server and handle response
  public async Upload_Process(): Promise<void> {
    if (this.ExcelData.length === 0) {
      this.ErrorMessage = 'No data available to process';
      return;
    }

    // Get only rows that are ready for processing (included rows from display)
    const selectedRows = this.ExcelData.filter(row => row.isSelected);

    if (selectedRows.length === 0) {
      this.ErrorMessage = 'There are no rows to process';
      return;
    }

    this.IsProcessing = true;
    this.ErrorMessage = '';

    try {
      // Prepare data payload as a list containing only included rows
      let fileBase64 = '';
      if (this.SelectedFile !== null && this.SelectedFile !== undefined) {
        fileBase64 = await this.File_ToBase64(this.SelectedFile);
      }

      // Generate API payload - list of included rows with metadata
      const apiPayload = selectedRows.map(row => ({
        LoanID: row.data['Loan ID'],
        PropertyName: row.data['Property Name'],
        Suite: row.data['Suite'],
        Floor: row.data['Floor'],
        Unit: row.data['Unit'],
        Lot: row.data['Lot'],
        Number: row.data['Number'],
        StreetName: row.data['Street Name'],
        StreetType: row.data['Street Type'],
        CitySuburb: row.data['City/Suburb'],
        State: row.data['State'],
        Postcode: row.data['Postcode'],
        Country: row.data['Country'],
        Purpose: row.data['Purpose'],
        Type: row.data['Type'],
        TitleType: row.data['Title Type'],
        Use: row.data['Use'],
        ValuerCompany: row.data['Valuer Company'],
        ValuerName: row.data['Valuer name'],
        ValuationAmount: row.data['Valuation amount'],
        InsurableValue: row.data['Insurable Value'],
        ValuationDate: row.data['Valuation date'],
        Insurer: row.data['Insurer'],
        PolicyNo: row.data['Policy No.'],
        PolicyType: row.data['Policy Type'],
        PolicyEndDate: row.data['Policy End Date'],
        InsuredAmount: row.data['Insured amount'],
        InsuranceInterestedParty: row.data['Insurance Interested party'],
        LetterToBeEmailed: row.data['Letter to be Emailed'],
        RowIndex: row.rowIndex
      }));

      const payload = {
        fileName: this.FileName,
        fileBase64: fileBase64,
        totalRows: this.ExcelData.length,
        includedRows: selectedRows.length,
        excludedRows: this.ExcelData.length - selectedRows.length,
        data: apiPayload
      };

      //Construct API Request
      const apiRequest = { LoanID: this.LoanID, FileName: this.FileName, FileBase64: fileBase64, Data: JSON.parse(JSON.stringify(apiPayload)) };

      //TODO:Un-comment following lines when working on server-side tickets
      /* this.apiService.APIData_Post(this.apiService.Endpoints.StaticDataController, StaticDataControllerMethods[StaticDataControllerMethods.ValidateAndUploadBulkSecurityUpload], apiRequest)
        .subscribe(apiResponse => {
          if (this.globalFunctions.isEmpty(apiResponse)) {
            this.clientDataStore.SetShowFullscreenLoading(false);
            return;
          }
          else {

            //Deserialize it into an class that we can understand
            const response = JSON.parse(JSON.stringify(apiResponse));

            //this.clientDataStore.SetShowFullscreenLoading(false);
            this.notifyService.Success_Show("Validation successful", "Success");

            const matchingValidationResults = response.ValidationResults.filter(v =>

              selectedRows.filter(r => r.rowIndex === v.rowIndex)
            );

            selectedRows.forEach(row => {
              const validation = response.ValidationResults.find(
                v => v.RowIndex === row.rowIndex
              );

              if (validation) {
                console.log("validation", validation);
                row.status = validation.Messages.join(' | ');
                row.isSelected = false;
              }
            });

            this.ProcessingComplete = true;
            this.IsProcessing = false;

            return;
          }
        }); */

    }
    catch (error) {
      this.ErrorMessage = 'Error processing upload. Please try again.';
      console.error('Processing error:', error);
      this.IsProcessing = false;
    }
  }

  public ResetInputs() {

    // Clear the uploaded file and data
    const fileInput = document.getElementById('excelFileInput') as HTMLInputElement | null;
    if (fileInput !== null && fileInput !== undefined) {
      fileInput.value = '';
    }

    this.SelectedFile = null;
    this.FileName = '';
    this.ExcelData = [];
    this.ColumnHeaders = [];
    this.IsFileImported = false;
    this.ErrorMessage = '';
    this.WarningMessage = '';
    this.ExtraColumns = [];
    this.MissingColumns = [];
    this.InvalidColumns = [];
    this.ExcludedRowsCount = 0;
    this.TotalRowsInFile = 0;
    this.AllRowsSelected = true;
    this.IsProcessing = false;
    this.ProcessingComplete = false;

    //Reset the flag too
    this.IsFileImported = false;

    this.IsValidated = false;
  }

  // Check if there is data available to process
  public Has_DataToProcess(): boolean {
    console.log("Has_DataToProcess", this.IsFileImported && this.ExcelData.length > 0 && !this.IsProcessing && this.MissingLoanIDCount === 0 && this.ExcludedRowsCount === 0);
    return this.IsFileImported && this.ExcelData.length > 0 && !this.IsProcessing && this.MissingLoanIDCount === 0 && this.ExcludedRowsCount === 0;
  }

  // Clear the uploaded file and data
  public File_Clear(): void {
    this.SelectedFile = null;
    this.FileName = '';
    this.ExcelData = [];
    this.ColumnHeaders = [];
    this.IsFileImported = false;
    this.ErrorMessage = '';
    this.WarningMessage = '';
    this.ExtraColumns = [];
    this.MissingColumns = [];
    this.InvalidColumns = [];
    this.ExcludedRowsCount = 0;
    this.TotalRowsInFile = 0;
    this.AllRowsSelected = true;
    this.IsProcessing = false;
    this.ProcessingComplete = false;
    this.MissingLoanIDCount = 0;

    // Reset file input
    const fileInput = document.getElementById('excelFileInput') as HTMLInputElement | null;
    if (fileInput !== null && fileInput !== undefined) {
      fileInput.value = '';
    }
  }

  // Close the modal
  public Modal_Close(): void {
    this.dialogRef.close();
  }
}
;
;
